/**
 * 实际控制人模块
 */

import React, { FC, useEffect, useMemo, useState } from 'react';
import { ProDetailList } from '@bedrock/eed-react';
import { Button, Radio, Tag } from '@bedrock/components';
import { ChevronDown, ChevronUp } from '@bedrock/icons-react';
import {
  FormModule,
  FormItemActualControllerDetailList,
  FormItemPersonName,
  FormItemPersonIdType,
  FormItemPersonId,
  FormItemShareholdingRatio,
  ClickableName,
  DataSourceTag,
} from '@purchase/supplier';
import { useExecutiveRelationModal } from '../../Qcc/useExecutiveRelationModal';

type DataSource = 'supplier' | 'thirdParty';

const ActualControllerModule: FC<{
  formDetail: any;
  isExternal?: boolean;
}> = ({ formDetail, isExternal = false }) => {
  const [actualControllerSource, setActualControllerSource] =
    useState<DataSource>('supplier');
  const [showHistoryControllers, setShowHistoryControllers] = useState(false);
  const { openModal, modalElement, hasRelationData } =
    useExecutiveRelationModal(formDetail);

  const supplierActualControllerCount = useMemo(
    () => formDetail?.vendorActualControllerInfos?.length ?? 0,
    [formDetail]
  );
  const qccActualControllerCount = useMemo(
    () => formDetail?.vendorQccActualControllerInfos?.length ?? 0,
    [formDetail]
  );
  const totalActualControllerCount =
    supplierActualControllerCount + qccActualControllerCount;

  // TODO(待后端 B5): vendorQccHistoryActualControllerInfos 字段尚未实现,
  // 上线前由后端在 VendorEntiretyDetailDto 中新增,结构同 VendorQccActualControllerInfoDto。
  const historyControllers =
    formDetail?.vendorQccHistoryActualControllerInfos ?? [];
  const hasHistoryControllers = historyControllers.length > 0;
  const qccControllerUpdateTime = formDetail?.qccDataFetchedAt;

  const thirdPartyListName = showHistoryControllers
    ? 'vendorQccActualControllerWithHistoryInfos'
    : 'vendorQccActualControllerInfos';

  // 当只有一方有数据时，自动切换到对应的数据源
  useEffect(() => {
    if (supplierActualControllerCount > 0 && qccActualControllerCount === 0) {
      setActualControllerSource('supplier');
    } else if (
      supplierActualControllerCount === 0 &&
      qccActualControllerCount > 0
    ) {
      setActualControllerSource('thirdParty');
    }
  }, [supplierActualControllerCount, qccActualControllerCount]);

  return (
    <>
      <FormModule
        title="实际控制人"
        titleCount={totalActualControllerCount}
        proCardProps={{
          expandable: true,
          defaultExpanded: totalActualControllerCount > 0,
        }}
      >
        {!isExternal && (
          <div className="flex items-center justify-between mb-3">
            <Radio.Group
              value={actualControllerSource}
              onChange={(e) => setActualControllerSource(e.target.value)}
            >
              <Radio.Button value="supplier">
                供应商录入 ({supplierActualControllerCount})
              </Radio.Button>
              <Radio.Button value="thirdParty">
                企查查获取 ({qccActualControllerCount})
              </Radio.Button>
            </Radio.Group>
            {actualControllerSource === 'thirdParty' && (
              <DataSourceTag
                source="企查查"
                updateTime={qccControllerUpdateTime}
              />
            )}
          </div>
        )}

        {actualControllerSource === 'supplier' && (
          <FormItemActualControllerDetailList
            name="vendorActualControllerInfos"
            fieldProps={{ scroll: { x: 1120 } }}
          >
            <ProDetailList.Item
              label="#"
              name="#"
              meta={{ columnProps: { width: 50, fixed: true } }}
            >
              {(value, record, index) => index + 1}
            </ProDetailList.Item>
            <FormItemPersonName
              label="姓名"
              name="name"
              meta={{
                columnProps: {
                  width: 200,
                  className: 'font-semibold',
                  fixed: true,
                },
              }}
            />
            <FormItemPersonIdType label="证件类型" name="personIdType" />
            <FormItemPersonId label="证件号码" name="personId" />
          </FormItemActualControllerDetailList>
        )}

        {actualControllerSource === 'thirdParty' && (
          <>
            <FormItemActualControllerDetailList
              name={thirdPartyListName}
              fieldProps={{ scroll: { x: 1120 } }}
            >
              <ProDetailList.Item
                label="#"
                name="#"
                meta={{ columnProps: { width: 50, fixed: true } }}
              >
                {(value, record, index) => index + 1}
              </ProDetailList.Item>
              <ProDetailList.Item
                label="姓名"
                name="name"
                meta={{
                  columnProps: { width: 200, fixed: true },
                }}
              >
                {(value, record) => (
                  <span className="flex items-center gap-1">
                    <ClickableName
                      className="font-semibold"
                      name={value ?? ''}
                      clickable={hasRelationData(value ?? '')}
                      onClick={() => openModal({ name: value ?? '' })}
                    />
                    {record?.isHistory && (
                      <Tag color="light-grey" text="历史" size="xSmall" />
                    )}
                  </span>
                )}
              </ProDetailList.Item>
              <FormItemShareholdingRatio
                label="最终受益股份"
                name="percentTotal"
              />
              <FormItemShareholdingRatio
                label="表决权比例"
                name="controlPercent"
              />
            </FormItemActualControllerDetailList>

            {hasHistoryControllers && (
              <div className="mt-2 text-xs text-color-2">
                <Button
                  type="link"
                  onClick={() => setShowHistoryControllers((prev) => !prev)}
                  suffixIcon={
                    showHistoryControllers ? <ChevronUp /> : <ChevronDown />
                  }
                >
                  {showHistoryControllers ? '收起历史' : '展开历史'}
                </Button>
              </div>
            )}
          </>
        )}
      </FormModule>
      {modalElement}
    </>
  );
};

export default ActualControllerModule;
